import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { customerTeamsService } from '@/services/customer/customer-teams.service';
import {
  CreateCustomerTeamInput,
  CreateCustomerTeamResult,
  CustomerTeamInvitation,
  CustomerTeamMember,
  CustomerTeamStats,
  CustomerTeamSummary,
  InviteTeamMemberInput,
  UpdateTeamMemberRoleInput,
} from '@/types/customer-teams';

export const customerTeamKeys = {
  teams: ['customer', 'teams', 'list'] as const,
  memberships: (teamId: string) => ['customer', 'teams', teamId, 'memberships'] as const,
  invitations: (teamId: string) => ['customer', 'teams', teamId, 'invitations'] as const,
  metrics: ['customer', 'teams', 'metrics'] as const,
};

export function useCustomerTeams() {
  return useQuery<CustomerTeamSummary[]>({
    queryKey: customerTeamKeys.teams,
    queryFn: () => customerTeamsService.listTeams(),
    staleTime: 30_000,
  });
}

interface CreateTeamOptions {
  onSuccess?: (result: CreateCustomerTeamResult) => void;
  onError?: (error: unknown) => void;
}

export function useCreateTeam(options?: CreateTeamOptions) {
  const queryClient = useQueryClient();

  return useMutation<CreateCustomerTeamResult, unknown, CreateCustomerTeamInput>({
    mutationFn: (payload) => customerTeamsService.createTeam(payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: customerTeamKeys.teams });
      options?.onSuccess?.(result);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useTeamMemberships(teamId?: string) {
  const queryClient = useQueryClient();

  return useQuery<CustomerTeamMember[]>({
    queryKey: teamId ? customerTeamKeys.memberships(teamId) : customerTeamKeys.memberships('unknown'),
    queryFn: async () => {
      if (!teamId) {
        throw new Error('Team ID required to load members');
      }

      const teams = await queryClient.ensureQueryData<CustomerTeamSummary[]>({
        queryKey: customerTeamKeys.teams,
        queryFn: () => customerTeamsService.listTeams(),
      });

      const selectedTeam = teams.find((team) => team.id === teamId);
      if (selectedTeam?.memberships && selectedTeam.memberships.length && selectedTeam.membershipsComplete) {
        return selectedTeam.memberships;
      }

      const memberships = await customerTeamsService.getTeamMemberships(teamId);

      if (selectedTeam) {
        queryClient.setQueryData<CustomerTeamSummary[]>(customerTeamKeys.teams, (previous) => {
          if (!previous) {
            return previous;
          }

          return previous.map((team) =>
            team.id === teamId
              ? {
                  ...team,
                  memberships,
                  members: memberships.length,
                  membershipsComplete: true,
                }
              : team
          );
        });
      }

      return memberships;
    },
    enabled: Boolean(teamId),
    staleTime: 15_000,
  });
}

export function useTeamInvitations(teamId?: string) {
  return useQuery<CustomerTeamInvitation[]>({
    queryKey: teamId ? customerTeamKeys.invitations(teamId) : customerTeamKeys.invitations('unknown'),
    queryFn: () => {
      if (!teamId) {
        return Promise.reject(new Error('Team ID required to load invitations'));
      }
      return customerTeamsService.getTeamInvitations(teamId);
    },
    enabled: Boolean(teamId),
    staleTime: 10_000,
  });
}

export function useTeamMetrics() {
  return useQuery<CustomerTeamStats>({
    queryKey: customerTeamKeys.metrics,
    queryFn: () => customerTeamsService.getMetrics(),
    staleTime: 60_000,
  });
}

interface InviteOptions {
  onSuccess?: (invitation: CustomerTeamInvitation) => void;
}

export function useInviteTeamMember(teamId?: string, options?: InviteOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InviteTeamMemberInput) => {
      if (!teamId) {
        throw new Error('Team ID is required to invite a member');
      }
      return customerTeamsService.inviteMember(teamId, payload);
    },
    onSuccess: (invitation) => {
      if (teamId) {
        queryClient.invalidateQueries({ queryKey: customerTeamKeys.invitations(teamId) });
      }
      queryClient.invalidateQueries({ queryKey: customerTeamKeys.teams });
      options?.onSuccess?.(invitation);
      toast.success(`Invitation sent to ${invitation.email}`);
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error instanceof Error ? error.message : 'Unable to send invitation');
      toast.error(message);
    },
  });
}

export function useResendTeamInvitation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invitationId, message }: { invitationId: string; message?: string }) => {
      if (!teamId) {
        throw new Error('Team ID is required to resend an invitation');
      }

      return customerTeamsService.resendInvitation(teamId, invitationId, message);
    },
    onSuccess: (response) => {
      if (teamId) {
        queryClient.invalidateQueries({ queryKey: customerTeamKeys.invitations(teamId) });
      }
      queryClient.invalidateQueries({ queryKey: customerTeamKeys.teams });
      toast.success(response.message);
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { errors?: Array<{ detail?: string }>; message?: string } } })?.response?.data?.errors?.[0]?.detail ??
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error instanceof Error ? error.message : 'Unable to resend invitation');
      toast.error(message);
    },
  });
}

export function useRevokeTeamInvitation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invitationId, reason }: { invitationId: string; reason?: string }) => {
      if (!teamId) {
        throw new Error('Team ID is required to revoke an invitation');
      }

      return customerTeamsService.revokeInvitation(teamId, invitationId, reason);
    },
    onSuccess: (response) => {
      if (teamId) {
        queryClient.invalidateQueries({ queryKey: customerTeamKeys.invitations(teamId) });
      }
      queryClient.invalidateQueries({ queryKey: customerTeamKeys.teams });
      toast.success(response.message);
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { errors?: Array<{ detail?: string }>; message?: string } } })?.response?.data?.errors?.[0]?.detail ??
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error instanceof Error ? error.message : 'Unable to revoke invitation');
      toast.error(message);
    },
  });
}

export function useUpdateTeamMemberRole(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      role,
      creditLimit,
      permissions,
    }: {
      customerId: string;
      role: UpdateTeamMemberRoleInput['role'];
      creditLimit?: number | null;
      permissions?: Record<string, unknown>;
    }) => {
      if (!teamId) {
        throw new Error('Team ID is required to update a member');
      }
      return customerTeamsService.updateMemberRole(teamId, customerId, {
        role,
        creditLimit,
        permissions,
      });
    },
    onSuccess: (response, variables) => {
      if (teamId) {
        const { customerId, role, creditLimit } = variables;

        queryClient.setQueryData<CustomerTeamMember[]>(customerTeamKeys.memberships(teamId), (previous) => {
          if (!previous) {
            return previous;
          }

          return previous.map((member) =>
            member.customerId === customerId
              ? {
                  ...member,
                  role,
                  creditLimit: creditLimit !== undefined ? creditLimit : member.creditLimit,
                }
              : member
          );
        });

        queryClient.setQueryData<CustomerTeamSummary[]>(customerTeamKeys.teams, (previous) => {
          if (!previous) {
            return previous;
          }

          return previous.map((team) => {
            if (team.id !== teamId) {
              return team;
            }

            const updatedMemberships = team.memberships
              ? team.memberships.map((member) =>
                  member.customerId === customerId
                    ? {
                        ...member,
                        role,
                        creditLimit: creditLimit !== undefined ? creditLimit : member.creditLimit,
                      }
                    : member
                )
              : team.memberships;

            return {
              ...team,
              memberships: updatedMemberships,
              membershipsComplete: false,
            };
          });
        });

        queryClient.invalidateQueries({ queryKey: customerTeamKeys.memberships(teamId) }).catch((error) => {
          console.warn('Failed to invalidate team memberships after update:', error);
        });
      }

      queryClient.invalidateQueries({ queryKey: customerTeamKeys.teams }).catch((error) => {
        console.warn('Failed to invalidate team list after member update:', error);
      });

      toast.success(response?.message ?? 'Member settings updated');
      return variables;
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error instanceof Error ? error.message : 'Unable to update member role');
      toast.error(message);
    },
  });
}

export function useRemoveTeamMember(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      if (!teamId) {
        throw new Error('Team ID is required to remove a member');
      }
      return customerTeamsService.removeMember(teamId, customerId);
    },
    onSuccess: (_, customerId) => {
      if (teamId) {
        queryClient.setQueryData<CustomerTeamMember[]>(customerTeamKeys.memberships(teamId), (previous) => {
          if (!previous) {
            return previous;
          }

          return previous.filter((member) => member.customerId !== customerId);
        });

        queryClient.setQueryData<CustomerTeamSummary[]>(customerTeamKeys.teams, (previous) => {
          if (!previous) {
            return previous;
          }

          return previous.map((team) => {
            if (team.id !== teamId) {
              return team;
            }

            const updatedMemberships = team.memberships?.filter((member) => member.customerId !== customerId);
            const updatedMembersCount = updatedMemberships
              ? updatedMemberships.length
              : Math.max(0, team.members - 1);

            return {
              ...team,
              members: updatedMembersCount,
              memberships: updatedMemberships,
              membershipsComplete: false,
            };
          });
        });

        queryClient.invalidateQueries({ queryKey: customerTeamKeys.memberships(teamId) });
      }
      queryClient.invalidateQueries({ queryKey: customerTeamKeys.teams });
      queryClient.invalidateQueries({ queryKey: customerTeamKeys.metrics });
      toast.success('Member removed from the team');
      return customerId;
    },
    onError: (error: unknown) => {
      toast.error(resolveRemoveMemberErrorMessage(error));
    },
  });
}

function resolveRemoveMemberErrorMessage(error: unknown): string {
  const responseData = (error as { response?: { data?: { message?: string; errors?: Array<{ detail?: string }> } } })
    ?.response?.data;
  if (responseData?.message) {
    return responseData.message;
  }

  const responseDetail = responseData?.errors?.[0]?.detail;
  if (responseDetail) {
    return responseDetail;
  }

  const apiError = error as { message?: string; errors?: Record<string, string[]> };
  const apiErrorMessages = apiError?.errors ? Object.values(apiError.errors).flat() : [];
  const firstApiErrorMessage = apiErrorMessages.find((message) => Boolean(message));
  if (firstApiErrorMessage) {
    return firstApiErrorMessage;
  }

  if (apiError?.message) {
    return apiError.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to remove member';
}
